import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import GameCard from '../game-library-dashboard/components/GameCard';
import LoadingSkeleton from '../game-library-dashboard/components/LoadingSkeleton';
import gameService from '../../utils/gameService';
import { Search, Filter, SortAsc } from 'lucide-react';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('relevance');

  const query = searchParams.get('q') || '';

  useEffect(() => {
    setSearchTerm(query);
    if (query.trim()) {
      performSearch(query);
    } else {
      setSearchResults([]);
    }
  }, [query]);

  const performSearch = async (term) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await gameService.searchGames(term, 50);
      if (result.success) {
        const transformedGames = result.data.map(game => ({
          id: game.id,
          title: game.title,
          studio: game.gaming_studios?.name || 'Unknown Studio',
          coverImage: game.cover_image_url,
          rating: game.average_rating,
          genres: game.game_category_mappings?.map(mapping => mapping.game_categories?.name) || [],
          playTime: `${game.estimated_play_time}h`,
          tier: game.tier || 'premium',
          isNew: (Date.now() - new Date(game.published_at)) < (30 * 24 * 60 * 60 * 1000),
          isFavorited: false,
          playCount: game.play_count || 0,
          publishedAt: game.published_at
        }));
        
        setSearchResults(transformedGames);
      } else {
        setError(result.error || 'Failed to search games');
      }
    } catch (err) {
      setError('Failed to search games');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const sortedResults = [...searchResults].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'newest':
        return new Date(b.publishedAt) - new Date(a.publishedAt);
      case 'popular':
        return (b.playCount || 0) - (a.playCount || 0);
      default: // relevance
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{query ? `Search: ${query}` : 'Search Games'} - Solis Games</title>
        <meta name="description" content={`Search results for "${query}" on Solis Games`} />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 py-8">
          {/* Search Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-4">
              {query ? `Search Results for "${query}"` : 'Search Games'}
            </h1>
            
            {/* Search Form */}
            <form onSubmit={handleNewSearch} className="mb-6">
              <div className="relative max-w-2xl">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search for games, studios, or genres..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Search
                </button>
              </div>
            </form>

            {/* Results Info and Controls */}
            {query && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-muted-foreground">
                  {loading ? (
                    'Searching...'
                  ) : error ? (
                    <span className="text-destructive">{error}</span>
                  ) : (
                    `${searchResults.length} ${searchResults.length === 1 ? 'result' : 'results'} found`
                  )}
                </div>
                
                {searchResults.length > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <SortAsc className="w-4 h-4 text-muted-foreground" />
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="relevance">Relevance</option>
                        <option value="title">Title A-Z</option>
                        <option value="rating">Highest Rated</option>
                        <option value="newest">Newest</option>
                        <option value="popular">Most Popular</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-destructive mb-4">{error}</div>
              <button 
                onClick={() => performSearch(query)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
              >
                Try Again
              </button>
            </div>
          ) : query && searchResults.length === 0 ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">No Results Found</h2>
              <p className="text-muted-foreground mb-6">
                No games found for "{query}". Try different keywords or browse our categories.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/game-library-dashboard')}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Browse All Games
                </button>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    navigate('/search');
                  }}
                  className="px-6 py-3 border border-border text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                >
                  Clear Search
                </button>
              </div>
            </div>
          ) : !query ? (
            <div className="text-center py-12">
              <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Search Games</h2>
              <p className="text-muted-foreground mb-6">
                Enter a search term above to find games, studios, or genres.
              </p>
              <button
                onClick={() => navigate('/game-library-dashboard')}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Browse All Games
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sortedResults.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  showFavorite={true}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchResults;
